import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";

export default function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    cartTotal,
    cartCount,
    deliveryFee,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    user,
  } = useApp();

  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Products", path: "/products" },
    { label: "Cart", path: null },
  ];

  if (cart.length === 0) {
    return (
      <div>
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Breadcrumbs items={breadcrumbs} />
          <div className="py-16 text-center">
            <div className="text-7xl mb-4">🛒</div>
            <h2 className="text-3xl font-extrabold mb-3">Your cart is empty</h2>
            <p className="text-gray-500 mb-8">
              Add some fresh juices and salads to your cart!
            </p>
            <button
              onClick={() => navigate("/products")}
              className="btn-primary text-lg px-8 py-3"
            >
              Browse Menu
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbs} />

        <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold">Your Cart 🛒</h1>
            <p className="text-gray-500">
              {cartCount} item{cartCount !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={clearCart}
            className="text-sm border-2 border-red-400 text-red-400 rounded-full px-4 py-1.5 font-bold hover:bg-red-50 transition-colors"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="md:col-span-2 flex flex-col gap-3">
            {cart.map((item) => (
              <div key={item.productId} className="glass-card p-4">
                <div className="flex gap-4 items-start">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 rounded-xl object-cover shrink-0"
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/80x80/e8f5e9/2D8B4E?text=🥤";
                    }}
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-base">{item.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">
                      ₹{item.price} each
                    </p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center border-2 border-green-500 rounded-full overflow-hidden">
                        <button
                          onClick={() =>
                            updateCartQuantity(
                              item.productId,
                              item.quantity - 1,
                            )
                          }
                          className="w-8 h-8 flex items-center justify-center text-green-500 font-bold hover:bg-green-500/10"
                        >
                          −
                        </button>
                        <span className="font-extrabold px-3 text-green-500">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateCartQuantity(
                              item.productId,
                              item.quantity + 1,
                            )
                          }
                          className="w-8 h-8 flex items-center justify-center text-green-500 font-bold hover:bg-green-500/10"
                        >
                          +
                        </button>
                      </div>
                      <span className="font-extrabold text-green-500">
                        ₹{(item.price * item.quantity).toFixed(0)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.productId)}
                    className="text-red-400 hover:text-red-600 p-1"
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}

            <button
              onClick={() => navigate("/products")}
              className="btn-outline-green self-start mt-2 text-sm"
            >
              ← Continue Shopping
            </button>
          </div>

          {/* Order Summary */}
          <div>
            <div className="glass-card p-5 sticky top-24">
              <h3 className="font-extrabold text-lg mb-4">Order Summary</h3>

              {cartTotal >= 199 && cartTotal < 299 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-blue-700">
                  🎉 Add ₹{299 - cartTotal} more for FREE delivery!
                </div>
              )}
              {cartTotal >= 299 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 text-sm text-green-700">
                  🎉 You've unlocked FREE delivery!
                </div>
              )}

              <div className="flex flex-col gap-2 mb-3">
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    className="flex justify-between text-sm"
                  >
                    <span className="text-gray-500">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="font-semibold">
                      ₹{(item.price * item.quantity).toFixed(0)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-3 mb-3">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-semibold">₹{cartTotal.toFixed(0)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500 flex items-center gap-1">
                    🚚 Delivery
                  </span>
                  {deliveryFee === 0 ? (
                    <span className="chip bg-green-100 text-green-600 text-xs font-bold">
                      FREE
                    </span>
                  ) : (
                    <span className="font-semibold">₹{deliveryFee}</span>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3 flex justify-between mb-5">
                <span className="font-extrabold text-lg">Total</span>
                <span className="font-extrabold text-lg text-green-500">
                  ₹{(cartTotal + deliveryFee).toFixed(0)}
                </span>
              </div>

              <button
                onClick={() =>
                  user ? navigate("/checkout") : navigate("/login")
                }
                className="btn-primary w-full flex items-center justify-center gap-2 py-3.5 text-base font-extrabold"
              >
                {user ? "Proceed to Checkout" : "Login to Checkout"} →
              </button>

              <div className="flex justify-center gap-3 mt-3">
                {["🔒 Secure", "⚡ Quick Delivery", "🌿 Fresh"].map((tag) => (
                  <span key={tag} className="text-xs text-gray-400">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
